import { FC, useMemo } from "react";
import Translate from "components/Translate/Translate";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { BehavioralHealthDialogCrisis } from "./BehavioralHealthDialogCrisis";
import type { BehavioralAssessment } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import { BehavioralAssessmentDialogStructure } from "./BehavioralAssessmentDialogStructure";
import { BehavioralAssessmentActionSwitch } from "./BehavioralAssessmentActionSwitch";
import { BehavioralHealthDialogAssessmentStyled } from "./BehavioralHealthDialogAssessment.styled";

const {
  CenterEl,
  BehavioralAssessmentWrapper,
  BehavioralAssessmentTitle,
  BehavioralAssessmentDescription,
  BehavioralAssessmentAction
} = BehavioralHealthDialogAssessmentStyled;

interface HBehavioralHealthDialogAssessmentProps {
  assessment: BehavioralAssessment;
}

/**
 * Assessment dialog page of behavioral questionnaire (GAD-7, PHQ-8)
 *
 * General structure: Title, Message, ActionButton, Crisis hotline(optional)
 * https://www.figma.com/design/kUt51hgo6pKLtwoKEkih6M/%E2%9C%94-App?node-id=13044-21417&m=dev
 *
 * @param assessment
 * @constructor
 */
export const BehavioralHealthDialogAssessment: FC<
  HBehavioralHealthDialogAssessmentProps
> = ({ assessment }) => {
  const [, { firstName }] = useBlocNext(UserPreferencesCubit);

  const structure = useMemo<BehavioralAssessmentDialogStructure>(() => {
    switch (assessment) {
      case "allRight":
        return {
          title: "behavioralHealthDialog_title_allRight",
          description: "behavioralHealthDialog_description_allRight",
          action: "behavioralHealthDialog_action_complete",
          crisisRequired: false
        };
      case "maybe":
        return {
          title: "behavioralHealthDialog_title_maybe",
          description: "behavioralHealthDialog_description_maybe",
          action: "behavioralHealthDialog_action_connectWithLyra",
          crisisRequired: true
        };
      case "recommend":
        return {
          title: "behavioralHealthDialog_title_recommend",
          description: "behavioralHealthDialog_description_recommend",
          action: "behavioralHealthDialog_action_connectWithLyra",
          crisisRequired: true
        };
      default:
        throw new Error("Unknown behavioral health dialog assessment.");
    }
  }, [assessment]);

  return (
    <CenterEl>
      <BehavioralAssessmentWrapper>
        <BehavioralAssessmentTitle>
          <Translate msg={structure.title} variables={{ firstName }} />
        </BehavioralAssessmentTitle>
        <BehavioralAssessmentDescription>
          <Translate msg={structure.description} />
        </BehavioralAssessmentDescription>
        <BehavioralAssessmentAction>
          <BehavioralAssessmentActionSwitch
            assessment={assessment}
            structure={structure}
          />
        </BehavioralAssessmentAction>
        {structure.crisisRequired && <BehavioralHealthDialogCrisis />}
      </BehavioralAssessmentWrapper>
    </CenterEl>
  );
};
